import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  ArrowRight,
  Lock,
  Users,
  Workflow,
  Plug,
  TrendingUp,
  ShieldCheck,
  FileCheck2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { Reveal } from "./Reveal";

const FACTORS = [
  { icon: Users, title: "Team size", desc: "The number of team members using Nova." },
  { icon: Workflow, title: "Workflows & operations", desc: "The types and number of workflows Nova will handle." },
  { icon: Plug, title: "Integrations & connections", desc: "The tools and systems Nova needs to connect to." },
  { icon: TrendingUp, title: "Usage & scale", desc: "How much work Nova automates and the volume it handles." },
];

const REASSURANCE = [
  { icon: ShieldCheck, title: "Secure & compliant", desc: "Enterprise-grade security you can trust." },
  { icon: FileCheck2, title: "Clear pricing", desc: "Know what your Nova setup includes." },
  { icon: RefreshCw, title: "Built to scale", desc: "Expand Nova as your operations grow." },
  { icon: Sparkles, title: "Simple & transparent", desc: "Pricing built around real operations." },
];

const CHECKS = [
  "Tailored to your business",
  "Connect your existing tools",
  "Scale at your pace",
  "Dedicated support",
];

export const Pricing = () => (
  <section
    id="pricing"
    data-testid="pricing-section"
    className="scroll-mt-36 bg-white"
  >
    <div className="mx-auto max-w-7xl px-5 sm:px-8 py-24 lg:py-32">
      <Reveal>
        <div className="flex items-stretch gap-6 lg:gap-10">
          <div className="shrink-0">
            <span
              data-testid="chapter-06-number"
              className="text-5xl lg:text-6xl font-extrabold tracking-tighter text-nova-blue leading-none"
            >
              06
            </span>
            <span className="block mt-3 text-[11px] font-bold tracking-[0.28em] uppercase text-nova-muted">
              Simple Plans
            </span>
          </div>
          <span className="w-px bg-slate-200 self-stretch" aria-hidden="true" />
          <div className="min-w-0">
            <h2 className="text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tighter leading-[1.04] text-nova-navy">
              Pricing That Grows
              <br />
              <span className="text-nova-blue">With Your Business.</span>
            </h2>
            <p className="mt-5 text-base lg:text-lg text-nova-slate leading-relaxed max-w-xl">
              Start small, add operations as you grow. No full technology stack
              required — Nova works with what you have.
            </p>
          </div>
        </div>
      </Reveal>

      <div className="mt-14 grid lg:grid-cols-[minmax(0,37fr)_minmax(0,63fr)] gap-6 items-stretch">
        <Reveal delay={0.1} className="h-full">
          <div
            data-testid="pricing-start-card"
            className="h-full rounded-[22px] border border-slate-200/80 bg-white p-8 lg:p-9 shadow-[0_10px_36px_rgb(10,25,47,0.06)] flex flex-col"
          >
            <p className="text-sm font-bold text-nova-navy">Plans start at</p>
            <div className="mt-3 flex items-baseline gap-2.5">
              <span
                data-testid="pricing-start-price"
                className="text-6xl lg:text-7xl font-extrabold tracking-tighter text-nova-blue leading-none"
              >
                $99
              </span>
              <span className="text-base font-medium text-nova-muted">/ month</span>
            </div>
            <p className="mt-4 text-sm text-nova-slate leading-relaxed">
              Final pricing depends on your team size, workflows, and
              integrations.
            </p>

            <ul className="mt-7 space-y-3.5">
              {CHECKS.map((c) => (
                <li key={c} className="flex items-center gap-3">
                  <span className="w-5 h-5 rounded-full bg-nova-bluelight/70 flex items-center justify-center shrink-0">
                    <Check size={11} strokeWidth={3} className="text-nova-blue" />
                  </span>
                  <span className="text-sm font-medium text-nova-navy [overflow-wrap:anywhere]">{c}</span>
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-9">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  to="/request-demo"
                  data-testid="pricing-estimate-btn"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-nova-blue px-6 py-4 text-sm font-semibold text-white shadow-[0_12px_30px_rgb(37,99,235,0.35)] hover:bg-nova-bluedark transition-colors duration-300"
                >
                  Get your estimate
                  <ArrowRight size={16} strokeWidth={2} />
                </Link>
              </motion.div>
              <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-nova-muted">
                <Lock size={12} strokeWidth={2} />
                No credit card required
              </p>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.18} className="h-full">
          <div
            data-testid="pricing-factors-card"
            className="h-full rounded-[22px] bg-nova-navy p-8 lg:p-10 shadow-[0_20px_60px_-16px_rgb(10,25,47,0.5)]"
          >
            <h3 className="text-xl lg:text-2xl font-bold tracking-tight text-white">
              What affects your price?
            </h3>
            <p className="mt-1.5 text-sm text-slate-400">
              We build your plan around the way you work.
            </p>

            <div className="mt-6 divide-y divide-white/10">
              {FACTORS.map((f) => (
                <div key={f.title} className="flex items-start gap-5 py-5 first:pt-2 last:pb-0">
                  <span className="w-12 h-12 rounded-full bg-nova-blue flex items-center justify-center shrink-0 shadow-[0_8px_20px_rgb(37,99,235,0.4)]">
                    <f.icon size={20} strokeWidth={1.8} className="text-white" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-white [overflow-wrap:anywhere]">{f.title}</p>
                    <p className="mt-1 text-sm text-slate-400 leading-relaxed [overflow-wrap:anywhere]">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>

      <Reveal delay={0.22} className="mt-6">
        <div
          data-testid="pricing-reassurance-strip"
          className="rounded-[22px] border border-slate-200/70 bg-nova-ice/70 grid sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-slate-200/80"
        >
          {REASSURANCE.map((r) => (
            <div key={r.title} className="flex items-start gap-4 p-6 lg:p-7 min-w-0">
              <span className="w-11 h-11 rounded-2xl bg-white border border-nova-bluelight flex items-center justify-center shrink-0">
                <r.icon size={19} strokeWidth={1.6} className="text-nova-blue" />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-bold text-nova-navy [overflow-wrap:anywhere]">{r.title}</p>
                <p className="mt-1 text-xs text-nova-slate leading-relaxed [overflow-wrap:anywhere]">{r.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.28}>
        <p data-testid="pricing-disclaimer" className="mt-7 text-center text-xs text-nova-muted">
          All prices in USD. Taxes may apply.
        </p>
      </Reveal>
    </div>
  </section>
);
